import { MinDto } from './other.ts';

export interface ComplianceListItem {
    partyId: number;
    name: string;
    partyNumber?: string | null;
    complianceVerificationStatusTypeCode?: VerificationStatusType;
    managers: MinDto[];
    partners: MinDto[];
    complianceChecks: PartyComplianceCheckMinDto[];
    clientSince?: string | null;
    createdAt?: string | null;
}

export interface PartyComplianceCheckMinDto {
    complianceCheckName: string;
    complianceCheckStatusTypeCode: CheckStatusType;
    isManuallyEntered: boolean;
    complianceCheckTypeCode: CheckType;
}

export interface PartyComplianceCheckDto extends PartyComplianceCheckMinDto {
    id: number;
    valueText: string;
    isRequiredField: boolean;
    isManuallyResolvable: boolean;
    resolvedByEntityName?: string;
    resolvedByEntityId?: number;
    resolvedAt?: Date;
    resolvedBy?: string;
    orderNum: number;
    expiresAt?: string | null;
    isApproachingExpiry?: boolean;
    daysUntilExpiry?: number | null;
}

export interface PartyComplianceCheckUpdateDto extends PartyComplianceCheckCreateDto {
    id?: number;
}

export interface PartyComplianceCheckCreateDto {
    partyId: number;
    complianceCheckName?: string;
    valueText: string;
    complianceCheckStatusTypeCode: CheckStatusType;
}

export interface PartyComplianceCheckForm {
    id?: number;
    complianceCheckName?: string;
    valueText: string;
    status: boolean;
}

export interface PartyRiskFactorDto {
    id: number;
    riskFactorTypeCode: RiskFactorType;
    matchingRiskProfileTypeCode: RiskProfileType;
    orderNum: number;
    comment?: string;
    isAutomatic: boolean;
}

export enum VerificationStatusType {
    NOT_NEEDED = 'NOT_NEEDED',
    NOT_VERIFIED = 'NOT_VERIFIED',
    VERIFIED = 'VERIFIED',
    EXPIRED = 'EXPIRED',
}

export enum CheckStatusType {
    PASSED = 'PASSED',
    EXPIRED = 'EXPIRED',
    ATTENTION_NEEDED = 'ATTENTION_NEEDED',
}

export enum RiskProfileType {
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH',
    TO_BE_ASSESSED = 'TO_BE_ASSESSED',
    STOP_ONBOARDING = 'STOP_ONBOARDING',
}

enum RiskFactorType {
    HIGH_RISK_JURISDICTION = 'HIGH_RISK_JURISDICTION',
    HIGH_RISK_BUSINESS_AREA = 'HIGH_RISK_BUSINESS_AREA',
    SHELL_COMPANY = 'SHELL_COMPANY',
    UBO_IS_PEP = 'UBO_IS_PEP',
    CLIENT_IS_PEP = 'CLIENT_IS_PEP',
    POSSIBLE_ML_TF_PF = 'POSSIBLE_ML_TF_PF',
    EVADING_PROVIDING_INFORMATION = 'EVADING_PROVIDING_INFORMATION',
    STATE_GOV_PUBLIC_PERSON = 'STATE_GOV_PUBLIC_PERSON',
    PUBLICLY_LISTED = 'PUBLICLY_LISTED',
    UNDER_SANCTIONS = 'UNDER_SANCTIONS',
    UBO_UNDER_SANCTIONS = 'UBO_UNDER_SANCTIONS',
    CONNECTION_TO_SANCTIONED_ENTITY = 'CONNECTION_TO_SANCTIONED_ENTITY',
    PARTNERS_IN_HIGH_RISK_JURISDICTION = 'PARTNERS_IN_HIGH_RISK_JURISDICTION ',
    SUSPICIOS_UBO_CREDIBILITY = 'SUSPICIOS_UBO_CREDIBILITY',
}

// ─── Compliance Auto-Blocking Types ─────────────────────────────────────────

export enum ComplianceBlockingSeverity {
    CRITICAL = 'CRITICAL',
    HIGH = 'HIGH',
    MEDIUM = 'MEDIUM',
    LOW = 'LOW',
}

enum ComplianceBlockingRuleCode {
    PERSONA_NON_GRATA = 'PERSONA_NON_GRATA',
    RISK_PROFILE_STOP_ONBOARDING = 'RISK_PROFILE_STOP_ONBOARDING',
    RISK_FACTOR_UNDER_SANCTIONS = 'RISK_FACTOR_UNDER_SANCTIONS',
    RISK_FACTOR_UBO_UNDER_SANCTIONS = 'RISK_FACTOR_UBO_UNDER_SANCTIONS',
    RISK_FACTOR_CONNECTION_TO_SANCTIONED_ENTITY = 'RISK_FACTOR_CONNECTION_TO_SANCTIONED_ENTITY',
    RISK_FACTOR_POSSIBLE_ML_TF_PF = 'RISK_FACTOR_POSSIBLE_ML_TF_PF',
    RISK_FACTOR_EVADING_PROVIDING_INFORMATION = 'RISK_FACTOR_EVADING_PROVIDING_INFORMATION',
    RELATED_PARTY_SANCTIONED_OR_PNG = 'RELATED_PARTY_SANCTIONED_OR_PNG',
    CLIENT_NOT_VERIFIED = 'CLIENT_NOT_VERIFIED',
    RISK_PROFILE_HIGH = 'RISK_PROFILE_HIGH',
    RISK_PROFILE_MEDIUM = 'RISK_PROFILE_MEDIUM',
    RISK_PROFILE_TO_BE_ASSESSED = 'RISK_PROFILE_TO_BE_ASSESSED',
    RISK_FACTOR_CLIENT_IS_PEP = 'RISK_FACTOR_CLIENT_IS_PEP',
    RISK_FACTOR_UBO_IS_PEP = 'RISK_FACTOR_UBO_IS_PEP',
    CHECK_EXPIRED_UBO_DETERMINED = 'CHECK_EXPIRED_UBO_DETERMINED',
    CHECK_EXPIRED_AML_FILLED = 'CHECK_EXPIRED_AML_FILLED',
    CHECK_EXPIRED_SANCTION_CHECK = 'CHECK_EXPIRED_SANCTION_CHECK',
    CHECK_ATTENTION_NEEDED_REQUIRED_FIELDS = 'CHECK_ATTENTION_NEEDED_REQUIRED_FIELDS',
    CHECK_ATTENTION_NEEDED_MANUAL = 'CHECK_ATTENTION_NEEDED_MANUAL',
    CHECK_AML_FORM_MISSING = 'CHECK_AML_FORM_MISSING',
    CHECK_UBO_FORM_MISSING = 'CHECK_UBO_FORM_MISSING',
    CHECK_SANCTION_CHECK_NOT_DONE = 'CHECK_SANCTION_CHECK_NOT_DONE',
}

export enum ComplianceBlockStatus {
    ACTIVE = 'ACTIVE',
    PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
    RESOLVED = 'RESOLVED',
}

export interface ComplianceBlockActiveRuleDto {
    id: number;
    ruleCode: ComplianceBlockingRuleCode;
    status: ComplianceBlockStatus;
    severity: ComplianceBlockingSeverity;
    blocksCases: boolean;
    blocksTimecards: boolean;
    blocksAmlTimecards: boolean;
    triggeredAt: string;
    resolvedAt?: string | null;
}

export interface ComplianceBlockHistoryDto {
    id: number;
    ruleCode: ComplianceBlockingRuleCode;
    status: ComplianceBlockStatus;
    severity: ComplianceBlockingSeverity;
    blocksCases: boolean;
    blocksTimecards: boolean;
    blocksAmlTimecards: boolean;
    triggeredAt: string;
    resolvedAt?: string | null;
    resolvedByEmployee?: { id: number; name: string } | null;
    acknowledgedBy?: string | null;
    acknowledgedByEmployee?: { id: number; name: string } | null;
    acknowledgedAt?: string | null;
    comment?: string | null;
    isActive: boolean;
}

export interface ComplianceBlockingRuleDto {
    id: number;
    ruleCode: ComplianceBlockingRuleCode;
    isEnabled: boolean;
    blocksCases: boolean;
    blocksTimecards: boolean;
    blocksAmlTimecards: boolean;
    enforcementGracePeriodDays: number | null;
    requireAck: boolean;
    requireAckHighRisk: boolean;
    severity: ComplianceBlockingSeverity;
    orderNum: number;
    category: string;
}

export interface ComplianceBlockingRuleUpdateDto {
    id: number;
    isEnabled: boolean;
    blocksCases: boolean;
    blocksTimecards: boolean;
    blocksAmlTimecards: boolean;
    enforcementGracePeriodDays: number | null;
    requireAck: boolean;
    requireAckHighRisk: boolean;
    severity: ComplianceBlockingSeverity;
}

export enum CheckType {
    UBO_DETERMINED = 'UBO_DETERMINED',
    AML_FILLED = 'AML_FILLED',
    SERVICE_AGREEMENT_PRESENT = 'SERVICE_AGREEMENT_PRESENT',
}

// ─── Check Expiry Configuration Types ────────────────────────────────────────

export interface ComplianceCheckExpiryConfigDto {
    id: number;
    checkTypeCode: CheckType;
    isAutoExpiryEnabled: boolean;
    expiryPeriodMonths: number;
    warningPeriodDays: number;
    highRiskExpiryMonths: number | null;
    stopOnboardingExpiryMonths: number | null;
}

export interface ComplianceCheckExpiryConfigUpdateDto {
    isAutoExpiryEnabled: boolean;
    expiryPeriodMonths: number;
    warningPeriodDays: number;
    highRiskExpiryMonths: number | null;
    stopOnboardingExpiryMonths: number | null;
}

// ─── Compliance Dashboard Statistics ────────────────────────────────────────

export enum ComplianceStatisticsFilter {
    ONBOARDING_NEW_CLIENTS = 'ONBOARDING_NEW_CLIENTS',
    ACTIVE_NOT_VERIFIED = 'ACTIVE_NOT_VERIFIED',
    MISSING_CHECKS = 'MISSING_CHECKS',
    SOON_EXPIRING = 'SOON_EXPIRING',
    ACTIVE_PENDING_ACK = 'ACTIVE_PENDING_ACK',
}

export interface ComplianceSummaryDto {
    onboardingNewClients: number;
    activeNotVerified: number;
    missingChecks: number;
    soonExpiring: number;
    activePendingAck: number;
}

// ─── Related-party role settings ────────────────────────────────────────────

/** What a role lists: only a `LIST` role can be mandatory, and the `ENTITY` is never on a client identification. */
export type RelatedPartyRoleKind = 'LIST' | 'OWNER' | 'ENTITY';

export interface RelatedPartyRoleDto {
    code: string;
    name: string;
    kind: RelatedPartyRoleKind;
    clientTypes: string[];
    isLegallyRequired: boolean;
    actsForClient: boolean;
    isEnabled: boolean;
    /** `null` for the `ENTITY`. */
    identificationTierCodes: string[] | null;
    /** `null` for `OWNER` and `ENTITY` roles. */
    identificationRequiredTierCodes: string[] | null;
    /** `null` for the `ENTITY`. */
    identificationItemKeys: string[] | null;
}

/** A client identification tier: a live RISK_ASSESSMENT_LEVEL value. */
export interface RelatedPartyTierDto {
    code: string;
    name: string;
}

export interface RelatedPartyRoleSettingsDto {
    roles: RelatedPartyRoleDto[];
    /** In order. */
    tiers: RelatedPartyTierDto[];
    itemOptions: string[];
}

export type RelatedPartyRoleUpdateDto = Pick<
    RelatedPartyRoleDto,
    'code' | 'isEnabled' | 'identificationTierCodes' | 'identificationRequiredTierCodes' | 'identificationItemKeys'
>;
